// Free-on-YouTube landing page helpers (#588, #590).
//
// /youtube/<id> accepts two kinds of id:
//
//   - a YouTube video id (11 characters, e.g. 2tv4_26RA8U). Works today with
//     no backend support: the page plays it in the official YouTube embed.
//   - a FarmFlix catalogue uuid, the format the app's push and link.farmflix.tv
//     links use (spec docs/specs/youtube-crossover-ux-frs.md A-5, FR-PUSH-1).
//     It is looked up in the overview's `youtube` section, which the backend
//     does not send until #558 ships. Until then every uuid is unknown and
//     the page goes Home.
//
// The overview type below is a stand-in for the @booyaltd/core export B1
// (#558) will add, mirroring FFCustomerApp/src/Utils/YouTube.ts.

export const YOUTUBE_SOURCE_TYPE = "youtube";

export type YouTubeOverviewVideo = {
  id: string;
  name?: string;
  sources?: { type: string; src: string }[];
};

// Absent until the backend switches the feature on (FR-CAT-2).
export type OverviewWithYouTube = {
  youtube?: {
    channelId?: string;
    title?: string;
    videos?: YouTubeOverviewVideo[];
  };
};

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const CATALOGUE_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isYouTubeVideoId = (id: string | undefined): id is string =>
  !!id && YOUTUBE_VIDEO_ID.test(id);

export const isCatalogueUuid = (id: string | undefined): id is string =>
  !!id && CATALOGUE_UUID.test(id);

// The YouTube video id of a catalogue uuid, or undefined when the overview
// has no youtube section, no such video, or no youtube source on it.
export const findYouTubeVideoId = (
  overview: unknown,
  uuid: string,
): string | undefined => {
  const videos = (overview as OverviewWithYouTube | undefined)?.youtube?.videos;
  // UUIDs are case-insensitive; isCatalogueUuid accepts either case.
  const wanted = uuid.toLowerCase();
  const video = videos?.find((item) => item.id?.toLowerCase() === wanted);
  const src = video?.sources?.find(
    (source) => source.type === YOUTUBE_SOURCE_TYPE,
  )?.src;
  return isYouTubeVideoId(src) ? src : undefined;
};

// A YouTube-style start time from the query string: ?t=86, ?t=86s or
// ?start=86. Anything else (including 1m26s) is ignored.
export const parseStartSeconds = (search: string): number | undefined => {
  const params = new URLSearchParams(search);
  const raw = params.get("t") || params.get("start");
  const match = raw?.match(/^(\d+)s?$/);
  if (!match) {
    return undefined;
  }
  const seconds = parseInt(match[1], 10);
  return seconds > 0 ? seconds : undefined;
};

// The official IFrame embed with standard controls (the same embed host as
// OnThePull.js). Only the start time is added.
export const buildYouTubeEmbedUrl = (
  videoId: string,
  startSeconds?: number,
): string =>
  `https://www.youtube.com/embed/${videoId}${
    startSeconds ? `?start=${startSeconds}` : ""
  }`;

// "Watch on YouTube" link-out, tagged so YouTube Analytics can attribute the
// traffic to the website (spec FR-RAIL-3).
export const buildYouTubeWatchUrl = (videoId: string): string => {
  const params = new URLSearchParams({
    v: videoId,
    utm_source: "farmflix",
    utm_medium: "web",
    utm_campaign: "youtube-landing",
  });
  return `https://www.youtube.com/watch?${params.toString()}`;
};

// YouTube's public thumbnail, used only for the page's share preview.
export const youTubeThumbnailUrl = (videoId: string): string =>
  `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
